import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Web3Provider } from "@/providers/Web3Provider";

import DashboardLayout from "@/layouts/DashboardLayout";

import Login from "@/pages/auth/Login";
import Callback from "@/pages/auth/Callback";
import Onboarding from "@/pages/auth/Onboarding";

import DashboardHome from "@/pages/dashboard/Home";
import MyJobs from "@/pages/dashboard/MyJobs";
import AgentFleet from "@/pages/dashboard/AgentFleet";
import Messages from "@/pages/dashboard/Messages";
import WalletsOverview from "@/pages/dashboard/WalletsOverview";
import ApiKeysOverview from "@/pages/dashboard/ApiKeysOverview";
import ConnectedApps from "@/pages/dashboard/ConnectedApps";
import Financial from "@/pages/dashboard/Financial";
import Documentation from "@/pages/Documentation";
import ForumBoard from "@/pages/forum/ForumBoard";
import ForumThread from "@/pages/forum/ForumThread";

import JobBoard from "@/pages/jobs/JobBoard";
import PostJob from "@/pages/jobs/PostJob";
import EasyPostJob from "@/pages/jobs/EasyPostJob";
import JobPromptMatch from "@/pages/jobs/JobPromptMatch";
import JobDetail from "@/pages/jobs/JobDetail";
import EditJob from "@/pages/jobs/EditJob";
import AgentJobs from "@/pages/jobs/AgentJobs";

import AgentProfile from "@/pages/agents/AgentProfile";
import { PublicAgentProof } from "@/pages/agents/AgentProof";
import AgentEdit from "@/pages/agents/AgentEdit";
import RegisterAgent from "@/pages/agents/RegisterAgent";
import AgentClaim from "@/pages/agents/AgentClaim";
import AgentVerification from "@/pages/agents/AgentVerification";
import AgentApiKeys from "@/pages/agents/AgentApiKeys";
import AgentWallet from "@/pages/agents/AgentWallet";

import QuizList from "@/pages/quiz/QuizList";
import MyEvals from "@/pages/quiz/MyEvals";
import EvalBuilder from "@/pages/quiz/EvalBuilder";
import EvalSession from "@/pages/evals/EvalSession";
import EvalReport from "@/pages/evals/EvalReport";

import Activity from "@/pages/Activity";
import Profile from "@/pages/Profile";

import Disputes from "@/pages/admin/Disputes";
import DisputeDetail from "@/pages/admin/DisputeDetail";
import Templates from "@/pages/admin/Templates";
import Treasury from "@/pages/admin/Treasury";
import Blockchain from "@/pages/admin/Blockchain";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import Analytics from "@/pages/admin/Analytics";
import EvalTimingAnalytics from "@/pages/admin/EvalTimingAnalytics";
import AdminPromotions from "@/pages/admin/Promotions";
import EvalPackManagement from "@/pages/admin/EvalPackManagement";
import EvalItemsManagement from "@/pages/admin/EvalItemsManagement";
import CertificationRevenue from "@/pages/admin/CertificationRevenue";
import AdminPayments from "@/pages/admin/Payments";
import AdminUsers from "@/pages/admin/Users";
import AdminAgents from "@/pages/admin/Agents";
import AdminJobs from "@/pages/admin/Jobs";
import RecentBids from "@/pages/admin/RecentBids";
import LaunchOS from "@/pages/admin/LaunchOS";
import GTMTasks from "@/pages/admin/GTMTasks";
import OpenClawPage from "@/pages/admin/OpenClaw";
import Outreach from "@/pages/admin/Outreach";
import TweetPromotion from "@/pages/promotions/TweetPromotion";
import Referrals from "@/pages/dashboard/Referrals";
import NotFound from "@/pages/NotFound";

import RequireAuth from "@/components/auth/RequireAuth";
import AttributionCapture from "@/components/AttributionCapture";
import MarketingCapture from "@/components/MarketingCapture";

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false} storageKey="moltjobs-theme">
    <Web3Provider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AttributionCapture />
        <MarketingCapture />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<Callback />} />
          <Route path="/agent-claim" element={<AgentClaim />} />
          <Route path="/proof/agents/:id" element={<PublicAgentProof />} />

          <Route element={<RequireAuth />}>
            {/* Protected Routes (Onboarding) */}
            <Route path="/onboarding" element={<Onboarding />} />

            {/* Dashboard Layout Routes */}
            <Route element={<DashboardLayout />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              <Route path="/dashboard" element={<DashboardHome />} />
              <Route path="/dashboard/jobs" element={<MyJobs />} />
              <Route path="/dashboard/jobs/:id" element={<JobDetail />} />
              <Route path="/dashboard/agents" element={<AgentFleet />} />
              <Route path="/dashboard/messages" element={<Messages />} />
              <Route path="/dashboard/wallets" element={<WalletsOverview />} />
              <Route path="/dashboard/api-keys" element={<ApiKeysOverview />} />
              <Route path="/settings/connected-apps" element={<ConnectedApps />} />
              <Route path="/dashboard/financial" element={<Financial />} />

              <Route path="/dashboard/forum" element={<ForumBoard />} />
              <Route path="/dashboard/forum/:slug" element={<ForumThread />} />

              <Route path="/jobs" element={<JobBoard />} />
              <Route path="/jobs/find" element={<JobPromptMatch />} />
              <Route path="/jobs/new" element={<PostJob />} />
              <Route path="/jobs/new/easy" element={<EasyPostJob />} />
              <Route path="/dashboard/agent-jobs" element={<AgentJobs />} />
              <Route path="/jobs/:id" element={<JobDetail />} />
              <Route path="/jobs/:id/edit" element={<EditJob />} />

              <Route path="/agents" element={<Navigate to="/dashboard/agents" replace />} />
              <Route path="/agents/new" element={<RegisterAgent />} />
              <Route path="/agents/:id" element={<AgentProfile />} />
              <Route path="/agents/:id/edit" element={<AgentEdit />} />
              <Route path="/agents/:id/verification" element={<AgentVerification />} />
              <Route path="/agents/:id/api-keys" element={<AgentApiKeys />} />
              <Route path="/agents/:id/wallet" element={<AgentWallet />} />

              {/* Quiz routes (uses eval system backend) */}
              <Route path="/dashboard/quiz" element={<QuizList />} />
              <Route path="/dashboard/quiz/mine" element={<MyEvals />} />
              <Route path="/dashboard/quiz/build" element={<EvalBuilder />} />
              <Route path="/dashboard/quiz/build/:packId" element={<EvalBuilder />} />
              <Route path="/dashboard/quiz/:evalId" element={<EvalSession />} />
              <Route path="/dashboard/quiz/:evalId/report" element={<EvalReport />} />

<Route path="/activity" element={<Activity />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/docs" element={<Documentation />} />

              {/* Admin Routes */}
              <Route path="/dashboard/promotions" element={<TweetPromotion />} />
              <Route path="/dashboard/referrals" element={<Referrals />} />

              {/* Admin Routes */}
              <Route path="/admin/launch" element={<LaunchOS />} />
              <Route path="/admin/gtm" element={<GTMTasks />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/agents" element={<AdminAgents />} />
              <Route path="/admin/jobs" element={<AdminJobs />} />
              <Route path="/admin/bids" element={<RecentBids />} />
              <Route path="/admin/payments" element={<AdminPayments />} />
              <Route path="/admin/treasury" element={<Treasury />} />
              <Route path="/admin/blockchain" element={<Blockchain />} />
              <Route path="/admin/disputes" element={<Disputes />} />
              <Route path="/admin/disputes/:id" element={<DisputeDetail />} />
              <Route path="/admin/templates" element={<Templates />} />
              <Route path="/admin/analytics" element={<Analytics />} />
              <Route path="/admin/quiz-timing" element={<EvalTimingAnalytics />} />
              <Route path="/admin/promotions" element={<AdminPromotions />} />
              <Route path="/admin/quiz-packs" element={<EvalPackManagement />} />
              <Route path="/admin/quiz-items/:packId" element={<EvalItemsManagement />} />
              <Route path="/admin/cert-revenue" element={<CertificationRevenue />} />
              <Route path="/admin/openclaw" element={<OpenClawPage />} />
              <Route path="/admin/outreach" element={<Outreach />} />
              <Route path="/admin" element={<AdminDashboard />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
    </Web3Provider>
  </ThemeProvider>
);

export default App;
